import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMyPolicies,
  PolicySummary,
  PolicyDocumentType,
  getPolicyDocumentDownloadUrl,
  getMyClaims,
  ClaimSummary,
  getMyInvites,
  InviteSummary,
  getApplications,
  ApplicationSummary,
  getOpenChangeRequests,
  TermChangeRequest,
} from "../api/services";
import { useTranslation } from "react-i18next";
import ClaimedPolicyCard from "../components/dashboard/ClaimedPolicyCard";
import PhoneSupportCard from "../components/dashboard/PhoneSupportCard";
import { formatDate as formatDateOnly, parseLocalDate } from "@ui/shared";
import {
  chargeAmountForRenewalTerm,
  renewalProcessingDate as computeRenewalProcessingDate,
} from "@libs/quote";
import { formatCurrency } from "../utils/formatCurrency";

interface PolicyDocumentLink {
  type: PolicyDocumentType;
  labelKey: string;
}

const POLICY_DOCUMENTS: PolicyDocumentLink[] = [
  {
    type: "rental-letter-of-credit",
    labelKey: "dashboard.documentTypes.rentalLetterOfCredit",
  },
  {
    type: "automatic-billing-authorization",
    labelKey: "dashboard.documentTypes.automaticBillingAuthorization",
  },
  {
    type: "acknowledgement",
    labelKey: "dashboard.documentTypes.acknowledgement",
  },
];

const renewalProcessingDate = (startsOn: string | null): string => {
  // Offset lives in @libs/quote so this matches the cron's actual window.
  // It was hardcoded as 7 here while ProcessRenewalsTask used 14, which
  // told tenants the wrong date.
  const d = computeRenewalProcessingDate(startsOn);
  return d ? d.toLocaleDateString() : "—";
};

/**
 * Renewal CTA is hidden when `nextTerm.startsOn` is more than this many
 * days in the future. The renewal payment processes RENEWAL_WINDOW_DAYS
 * before start, so showing "Process Now" months ahead just confuses the
 * tenant.
 */
const RENEWAL_VISIBLE_WINDOW_DAYS = 100;

const renewalWithinWindow = (startsOn: string | null): boolean => {
  if (!startsOn) return false;
  // parseLocalDate handles YYYY-MM-DD as local-midnight so the day
  // delta computed against `Date.now()` matches the calendar.
  const start = parseLocalDate(startsOn);
  if (isNaN(start.getTime())) return false;
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntilStart = Math.floor(
    (start.getTime() - Date.now()) / msPerDay,
  );
  return daysUntilStart <= RENEWAL_VISIBLE_WINDOW_DAYS;
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [policies, setPolicies] = useState<PolicySummary[]>([]);
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [applications, setApplications] = useState<ApplicationSummary[]>([]);
  const [latestClaimByPolicy, setLatestClaimByPolicy] = useState<
    Record<string, ClaimSummary | undefined>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingDocs, setDownloadingDocs] = useState<Record<string, boolean>>({});
  // Requested changes to an active term that the tenant needs to pay for.
  const [changeRequests, setChangeRequests] = useState<TermChangeRequest[]>([]);

  // Loaded separately so a failure here never blanks the dashboard.
  useEffect(() => {
    let cancelled = false;
    getOpenChangeRequests()
      .then((res) => {
        if (!cancelled) setChangeRequests(res.data);
      })
      .catch((err) => console.error("Failed to load change requests", err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([getMyPolicies(), getMyInvites(), getApplications()])
      .then(async ([policiesRes, invitesRes, applicationsRes]) => {
        if (cancelled) return;
        setPolicies(policiesRes.data);
        setInvites(invitesRes.data);
        setApplications(applicationsRes.data);

        const claimsByPolicy: Record<string, ClaimSummary | undefined> = {};
        await Promise.all(
          policiesRes.data.map(async (policy) => {
            try {
              const claimsRes = await getMyClaims(policy.uuid);
              claimsByPolicy[policy.uuid] = claimsRes.data[0];
            } catch (err) {
              console.error(`Failed to load claims for ${policy.uuid}`, err);
            }
          }),
        );
        if (!cancelled) setLatestClaimByPolicy(claimsByPolicy);
        setError(null);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError("dashboard.errorLoading");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleDocumentDownload = async (
    policyUuid: string,
    documentType: PolicyDocumentType,
  ) => {
    const key = `${policyUuid}:${documentType}`;
    setDownloadingDocs((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await getPolicyDocumentDownloadUrl(policyUuid, documentType);
      window.open(res.data.downloadUrl, "_blank");
    } catch (err) {
      console.error("Error downloading document:", err);
    } finally {
      setDownloadingDocs((prev) => ({ ...prev, [key]: false }));
    }
  };

  // All callers below feed DATE-only fields (leaseStartDate, leaseEndDate,
  // policy.startsOn, policy.endsOn, nextTerm.startsOn). formatDateOnly
  // uses parseLocalDate so the display day stays stable across TZs.
  const formatDate = (s: string | null | undefined): string =>
    s
      ? formatDateOnly(s, { year: "numeric", month: "numeric", day: "numeric" })
      : "-";

  if (loading) {
    return <p className="text-sm text-muted">{t("dashboard.loading")}</p>;
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
        {t(error)}
      </div>
    );
  }

  return (
    <div>
      {/* Security Deposit Alternative Section */}
      <h2 className="mb-4 text-lg font-semibold text-primary">
        {t("dashboard.sdaTitle")}
      </h2>

      {invites.length > 0 && (
        <div className="mb-6 space-y-4">
          {invites.map((invite) => {
            const propertyName = invite.unit?.property?.name ?? "";
            const unitName = invite.unit?.name ?? "";
            const propertyAddress = invite.unit?.property?.address ?? "";

            return (
              <div
                key={invite.uuid}
                className="relative overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100"
              >
                {/* Tinted header: company name on the left, status chip
                    on the right. Gives the card a strong identity line
                    so the body text can be calm. */}
                <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
                  <div className="flex min-w-0 items-center gap-2 text-sm text-gray-700">
                    <svg
                      className="h-4 w-4 shrink-0 text-muted"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2m-16 0H3m4-3h2m2 0h2m-6-4h2m2 0h2m-6-4h2m2 0h2"
                      />
                    </svg>
                    <span className="truncate font-medium">
                      {invite.account?.companyName ??
                        t("dashboard.fallbackManager")}
                    </span>
                  </div>
                  <span className="shrink-0 rounded-full bg-info/10 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-info">
                    {t("dashboard.invite.statusChip")}
                  </span>
                </div>

                <div className="p-5">
                  <p className="text-lg font-semibold text-primary">
                    {propertyName} – {unitName}
                  </p>
                  {propertyAddress && (
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-muted">
                      <svg
                        className="h-3.5 w-3.5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="truncate">{propertyAddress}</span>
                    </p>
                  )}

                  <div className="my-4 border-t border-gray-100" />

                  <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {t("dashboard.invite.monthlyRent")}
                      </p>
                      <p className="mt-0.5 text-base font-semibold text-primary">
                        {formatCurrency(invite.monthlyRentAmount)}
                      </p>
                    </div>
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {t("dashboard.invite.lease")}
                      </p>
                      <p className="mt-0.5 text-base font-semibold text-primary">
                        {formatDate(invite.leaseStartDate)} –{" "}
                        {formatDate(invite.leaseEndDate)}
                      </p>
                    </div>
                    {invite.coverageAmount !== null && (
                      <div>
                        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                          <svg
                            className="h-3.5 w-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                          </svg>
                          {t("dashboard.coverage")}
                        </p>
                        <p className="mt-0.5 text-base font-semibold text-primary">
                          {formatCurrency(invite.coverageAmount)}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-5 flex justify-end">
                    <button
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light"
                      onClick={() =>
                        navigate(`/sda/${invite.uuid}/acknowledge`)
                      }
                    >
                      {t("dashboard.invite.continue")}
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {policies.length === 0 && invites.length === 0 ? (
        <p className="mb-10 text-sm text-muted">{t("dashboard.noPolicies")}</p>
      ) : policies.length === 0 ? null : (
        <div className="mb-10 space-y-4">
          {policies.map((policy) => {
            const claim = latestClaimByPolicy[policy.uuid];

            if (claim) {
              return (
                <ClaimedPolicyCard
                  key={policy.uuid}
                  policy={policy}
                  claim={claim}
                  documents={POLICY_DOCUMENTS}
                  downloadingDocs={downloadingDocs}
                  onDownloadDocument={handleDocumentDownload}
                />
              );
            }

            const propertyName = policy.unit?.property?.name ?? "";
            const unitName = policy.unit?.name ?? "";

            const propertyAddress = policy.unit?.property?.address ?? "";
            const isPending = policy.status === "pending";

            return (
              <div
                key={policy.uuid}
                className="relative overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100"
              >
                {/* Tinted header — matches the invite card. Status chip
                    flips between "Processing" (pending) and "Covered"
                    (active) so the card communicates state at a glance. */}
                <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
                  <div className="flex min-w-0 items-center gap-2 text-sm text-gray-700">
                    <svg
                      className="h-4 w-4 shrink-0 text-muted"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2m-16 0H3m4-3h2m2 0h2m-6-4h2m2 0h2m-6-4h2m2 0h2"
                      />
                    </svg>
                    <span className="truncate font-medium">
                      {policy.account?.companyName ??
                        t("dashboard.fallbackManager")}
                    </span>
                  </div>
                  <span
                    className={
                      isPending
                        ? "shrink-0 rounded-full bg-info/10 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-info"
                        : "shrink-0 rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-success"
                    }
                  >
                    {isPending
                      ? t("dashboard.policyCard.statusProcessing")
                      : t("dashboard.policyCard.statusCovered")}
                  </span>
                </div>

                <div className="p-5">
                  <p className="text-lg font-semibold text-primary">
                    {propertyName} – {unitName}
                  </p>
                  {propertyAddress && (
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-muted">
                      <svg
                        className="h-3.5 w-3.5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="truncate">{propertyAddress}</span>
                    </p>
                  )}

                  <div className="my-4 border-t border-gray-100" />

                  <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                    {/* Policy number is only meaningful once the policy
                        is active — hide while ACH is still settling. */}
                    {!isPending && (
                      <div>
                        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                          <svg
                            className="h-3.5 w-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z"
                            />
                          </svg>
                          {t("dashboard.policy")}
                        </p>
                        <p className="mt-0.5 text-base font-semibold text-primary">
                          {policy.name}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        {t("dashboard.coverage")}
                      </p>
                      <p className="mt-0.5 text-base font-semibold text-primary">
                        {formatCurrency(policy.coverageAmount)}
                      </p>
                    </div>
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {t("dashboard.effective")}
                      </p>
                      <p className="mt-0.5 text-base font-semibold text-primary">
                        {formatDate(policy.startsOn)} – {formatDate(policy.endsOn)}
                      </p>
                    </div>
                  </div>

                  {/* Policy Documents — hidden while pending. The PDFs
                      reference the (still-unassigned) policy number and
                      the coverage hasn't actually started yet, so
                      surfacing them mid-processing is misleading. */}
                  {policy.status !== "pending" && (
                    <div className="mt-5 border-t border-gray-100 pt-4">
                      <p className="mb-3 text-sm font-semibold text-primary">
                        {t("dashboard.documents")}
                      </p>
                      <div className="flex flex-wrap gap-x-5 gap-y-2">
                        {POLICY_DOCUMENTS.map((doc) => {
                          const key = `${policy.uuid}:${doc.type}`;
                          return (
                            <button
                              key={doc.type}
                              type="button"
                              className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-info underline hover:text-sky-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                              onClick={() =>
                                handleDocumentDownload(policy.uuid, doc.type)
                              }
                              disabled={downloadingDocs[key]}
                            >
                              <svg
                                className="h-4 w-4 shrink-0 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                              {downloadingDocs[key]
                                ? t("dashboard.downloading")
                                : t(doc.labelKey)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {policy.status !== "pending" &&
                    policy.nextTerm &&
                    (policy.nextTerm.status === "scheduled" ||
                      policy.nextTerm.status === "confirmed") &&
                    policy.nextTerm.total !== null &&
                    policy.nextTerm.total > 0 &&
                    // Show the CTA either when the renewal is within the
                    // visible window, OR when the landlord has explicitly
                    // confirmed the next term (regardless of how far out
                    // startsOn is — confirmation = "we want this paid
                    // now").
                    (renewalWithinWindow(policy.nextTerm.startsOn) ||
                      policy.nextTerm.status === "confirmed") && (
                      <div className="mt-4 flex items-center justify-between rounded-lg bg-sky-50 px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-info">
                            {t("dashboard.renewalBanner.title")}
                          </p>
                          <p className="text-xs text-gray-600">
                            {t("dashboard.renewalBanner.processingOn", {
                              date: renewalProcessingDate(
                                policy.nextTerm.startsOn,
                              ),
                            })}
                            {` · ${formatCurrency(
                              // Stored total is the 12-month figure;
                              // month-to-month policies are billed
                              // one quarter at a time. Helper returns
                              // total/4 for m2m, total as-is for fixed.
                              chargeAmountForRenewalTerm(
                                policy.nextTerm.total,
                                policy.nextTerm.renewalTerm,
                              ) ?? 0,
                            )}`}
                          </p>
                        </div>
                        <button
                          className="rounded-lg bg-info px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-sky-600 cursor-pointer"
                          onClick={() => navigate(`/policy/${policy.uuid}/renew`)}
                        >
                          {t("dashboard.renewalBanner.processNow")}
                        </button>
                      </div>
                    )}
                </div>

                {/* A requested change to the active term. Pending: the new
                    terms cost more and the tenant pays the difference (same
                    shape as the renewal CTA). Processing: their bank
                    payment is settling. */}
                {changeRequests
                  .filter((r) => r.policyUuid === policy.uuid)
                  .map((r) =>
                    r.status === "pending_payment" ? (
                      <div
                        key={r.uuid}
                        className="flex items-center justify-between gap-3 border-t border-amber-100 bg-amber-50 px-5 py-3"
                      >
                        <div>
                          <p className="text-sm font-medium text-warning">
                            {t("dashboard.changeRequestBanner.title")}
                          </p>
                          <p className="text-xs text-gray-600">
                            {t("dashboard.changeRequestBanner.body", {
                              amount: formatCurrency(r.difference),
                            })}
                          </p>
                        </div>
                        <button
                          className="shrink-0 cursor-pointer rounded-lg bg-warning px-4 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90"
                          onClick={() =>
                            navigate(`/policy/${policy.uuid}/change/${r.uuid}`)
                          }
                        >
                          {t("dashboard.changeRequestBanner.cta")}
                        </button>
                      </div>
                    ) : (
                      <div
                        key={r.uuid}
                        className="border-t border-sky-100 bg-sky-50 px-5 py-3 text-sm text-info"
                      >
                        {t("dashboard.changeRequestBanner.processing")}
                      </div>
                    ),
                  )}

                {/* ACH renewal is settling. Same shape as the initial-
                    purchase pending banner so the two pending states look
                    identical to the tenant — only the copy differs. */}
                {policy.nextTerm &&
                  policy.nextTerm.status === "processing" && (
                    <div className="flex items-center gap-2 border-t border-sky-100 bg-sky-50 px-5 py-3 text-sm">
                      <svg
                        className="h-4 w-4 shrink-0 text-info"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div>
                        <p className="font-medium text-info">
                          {t("dashboard.renewalProcessingBanner.title")}
                        </p>
                        <p className="text-xs text-gray-600">
                          {t("dashboard.renewalProcessingBanner.body")}
                        </p>
                      </div>
                    </div>
                  )}

                {isPending && (
                  <div className="flex items-center gap-2 border-t border-sky-100 bg-sky-50 px-5 py-3 text-sm">
                    <svg
                      className="h-4 w-4 shrink-0 text-info"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <p className="font-medium text-info">
                        {t("dashboard.pendingBanner.title")}
                      </p>
                      <p className="text-xs text-gray-600">
                        {t("dashboard.pendingBanner.body")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Applications Section — only shown when the tenant has applications. */}
      {applications.length > 0 && (
        <>
          <h2 className="mb-4 text-lg font-semibold text-primary">
            {t("dashboard.myApplications", "My Applications")}
          </h2>

          <div className="space-y-4">
            {applications.map((app) => (
              <div
                key={app.uuid}
                className="relative overflow-hidden rounded-xl bg-white shadow-sm"
              >
                <span
                  className={`absolute right-4 top-4 rounded-full px-2.5 py-0.5 text-xs font-medium text-white ${
                    app.status === "created"
                      ? "bg-primary"
                      : app.status === "in-progress"
                        ? "bg-info"
                        : "bg-success"
                  }`}
                >
                  {t(`status.${app.status}`)}
                </span>

                <div className="p-5">
                  <p className="text-base font-semibold text-primary">
                    {app.unit.property.name} – {app.unit.name}
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    {app.firstName} {app.lastName} ({app.email})
                  </p>
                  <p className="mt-1 text-sm text-muted">
                    {t("dashboard.moveInDate", "Move-in Date:")}&nbsp;
                    {formatDate(app.moveInDate)}
                  </p>
                  <div className="mt-4 flex justify-end">
                    <button
                      className="rounded-lg bg-accent px-5 py-2 text-sm font-medium text-black transition-colors hover:bg-accent-hover"
                      onClick={() => navigate(`/application/${app.uuid}`)}
                    >
                      {t("dashboard.view", "View")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <PhoneSupportCard />
    </div>
  );
};

export default Dashboard;
